import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCcw, Search, Filter } from "lucide-react";

import { adminApi } from "@/lib/api";
import { readToken, clearToken } from "@/lib/tokenStore";
import { LEAD_STATUSES } from "@/lib/constants";

import AdminLogin from "@/components/admin/AdminLogin";
import StatsGrid from "@/components/admin/StatsGrid";
import LeadsTable from "@/components/admin/LeadsTable";
import LeadDetailDrawer from "@/components/admin/LeadDetailDrawer";
import LegacyLeadsBanner from "@/components/journey/LegacyLeadsBanner";

function matchesSearch(item, term) {
  if (!term) return true;
  const t = term.toLowerCase();
  const haystack = [
    item.first_name,
    item.last_name,
    item.company,
    item.email,
    item.city_region,
  ];
  return haystack.some((v) => (v || "").toLowerCase().includes(t));
}

function applyFilters(items, statusFilter, search) {
  return items.filter((x) => {
    if (statusFilter && x.lead_status !== statusFilter) return false;
    return matchesSearch(x, search);
  });
}

function Dashboard({ token, onLogout }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const client = useMemo(() => adminApi(token), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [leads, st] = await Promise.all([
        client.get("/admin/leads"),
        client.get("/admin/stats"),
      ]);
      setItems(leads.data.items);
      setStats(st.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        onLogout();
      } else {
        console.error("Admin load failed:", err);
        toast.error("Daten konnten nicht geladen werden");
      }
    } finally {
      setLoading(false);
    }
  }, [client, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await client.patch(`/admin/leads/${id}`, { lead_status: status });
      setItems((prev) =>
        prev.map((x) => (x.id === id ? { ...x, lead_status: status } : x))
      );
      setSelected((prev) =>
        prev && prev.id === id ? { ...prev, lead_status: status } : prev
      );
      toast.success("Status aktualisiert");
    } catch (err) {
      console.error("Status update failed:", err);
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Lead wirklich löschen?")) return;
    try {
      await client.delete(`/admin/leads/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      setSelected((prev) => (prev && prev.id === id ? null : prev));
      toast.success("Gelöscht");
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const logout = async () => {
    try {
      await client.post("/admin/logout");
    } catch (err) {
      console.error("Logout request failed:", err);
    } finally {
      clearToken();
      onLogout();
    }
  };

  const toggleFilter = (value) =>
    setStatusFilter((prev) => (prev === value ? "" : value));

  const filtered = useMemo(
    () => applyFilters(items, statusFilter, search),
    [items, statusFilter, search]
  );

  return (
    <div className="min-h-screen bg-[#050607] text-[#F5F7F7]">
      <header className="border-b border-[rgba(255,255,255,0.08)] sticky top-0 bg-[#050607] z-30">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex flex-col leading-none">
              <span className="font-display text-base tracking-widest">PINE</span>
              <span className="font-display text-base tracking-widest -mt-1">SYSTEMS</span>
              <span className="h-px w-8 bg-[#18E096] mt-1" />
            </Link>
            <span className="overline ml-4">Admin · Leads</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/content" className="btn-secondary" data-testid="admin-content-link">
              Content Editor
            </Link>
            <button
              onClick={load}
              className="btn-secondary"
              data-testid="admin-refresh"
              type="button"
            >
              <RefreshCcw size={14} strokeWidth={1.5} /> Aktualisieren
            </button>
            <button
              onClick={logout}
              className="btn-secondary"
              data-testid="admin-logout"
              type="button"
            >
              <LogOut size={14} strokeWidth={1.5} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 md:px-10 py-8">
        <LegacyLeadsBanner />
        <StatsGrid
          stats={stats}
          activeFilter={statusFilter}
          onToggleFilter={toggleFilter}
        />

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[240px]">
            <Search
              size={16}
              strokeWidth={1.5}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#AEB7B7]"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suche nach Name, Firma, E-Mail, Region…"
              className="field pl-10"
              data-testid="admin-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={16} strokeWidth={1.5} className="text-[#AEB7B7]" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="field min-w-[220px]"
              data-testid="admin-filter"
            >
              <option value="">Alle Status</option>
              {LEAD_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <LeadsTable
          items={filtered}
          loading={loading}
          onSelect={setSelected}
          onDelete={remove}
        />
      </main>

      <LeadDetailDrawer
        lead={selected}
        token={token}
        onClose={() => setSelected(null)}
        onStatusChange={changeStatus}
      />
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => readToken());
  if (!token) return <AdminLogin onLogin={setToken} />;
  return <Dashboard token={token} onLogout={() => setToken("")} />;
}
